import type React from "react";
import { memo } from "react";
import { UI_ICONS } from "../icons";
import type { LegalType } from "./modals/LegalModal";

interface FooterProps {
	onStart: () => void;
	onPause: () => void;
	onStop: () => void;
	onPreferences: () => void;
	onHistory: () => void;
	onLegal?: (type: LegalType) => void;
	isStopping?: boolean;
	isRunning: boolean;
	isPaused: boolean;
}

/**
 * Footer component, memoized to prevent re-renders during transcription updates.
 * Requires stable callbacks from the parent to be effective.
 */
export const Footer: React.FC<FooterProps> = memo(
	({
		onStart,
		onPause,
		onStop,
		onPreferences,
		onHistory,
		onLegal,
		isStopping = false,
		isRunning,
		isPaused,
	}) => {
		return (
			<footer className="h-[var(--spacing-controls)] bg-card-bg border-t border-border-main flex flex-col items-center justify-center px-1.5 sm:px-4 sticky bottom-0 z-[100]">
				<div className="flex items-center justify-center gap-1 sm:gap-4 w-full">
					{!isRunning ? (
						<button
							type="button"
							id="start-btn"
							className="inline-flex items-center justify-center gap-1 sm:gap-2 px-2.5 sm:px-5 py-2 rounded-lg font-semibold text-[11px] sm:text-sm cursor-pointer transition-all duration-200 bg-primary text-white min-w-[72px] sm:min-w-[100px] hover:bg-primary-hover hover:-translate-y-0.5 active:translate-y-0 active:scale-95 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
							title="セッション開始 (Space)"
							aria-label="セッションを開始"
							onClick={onStart}
							disabled={isStopping}
						>
							<span role="img" aria-hidden="true">
								▶️
							</span>{" "}
							<span className="truncate">開始</span>
							<kbd className="hidden md:inline-flex ml-1 px-1.5 py-0.5 text-[10px] bg-white/20 rounded font-mono border-none">
								Space
							</kbd>
						</button>
					) : (
						<>
							<button
								type="button"
								id="pause-btn"
								className="inline-flex items-center justify-center gap-1 sm:gap-2 px-2.5 sm:px-5 py-2 rounded-lg font-semibold text-[11px] sm:text-sm cursor-pointer transition-all duration-200 bg-slate-700 border border-border-main text-text-main min-w-[72px] sm:min-w-[100px] hover:bg-slate-600 hover:-translate-y-0.5 active:translate-y-0 active:scale-95 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
								title="セッション一時停止"
								aria-label={
									isPaused ? "セッションを再開" : "セッションを一時停止"
								}
								onClick={onPause}
								disabled={isStopping}
							>
								<span role="img" aria-hidden="true">
									{isPaused ? "▶️" : "⏸️"}
								</span>{" "}
								<span className="truncate">
									{isPaused ? "再開" : "一時停止"}
								</span>
							</button>
							<button
								type="button"
								id="stop-btn"
								className={`inline-flex items-center justify-center gap-1 sm:gap-2 px-2.5 sm:px-5 py-2 rounded-lg font-semibold text-[11px] sm:text-sm cursor-pointer transition-all duration-200 min-w-[72px] sm:min-w-[100px] border border-border-main ${
									isStopping
										? "bg-slate-800 text-slate-400 cursor-not-allowed"
										: "bg-slate-700 text-text-main hover:bg-slate-600 hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
								} focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0`}
								title="セッション停止 (Space)"
								aria-label="セッションを停止"
								onClick={onStop}
								disabled={isStopping}
							>
								<span role="img" aria-hidden="true">
									{isStopping ? "⌛" : "⏹️"}
								</span>{" "}
								<span className="truncate">
									{isStopping ? "停止中" : "停止"}
								</span>
								{!isStopping && (
									<kbd className="hidden md:inline-flex ml-1 px-1.5 py-0.5 text-[10px] bg-white/20 rounded font-mono border-none">
										Space
									</kbd>
								)}
							</button>
						</>
					)}
					<button
						type="button"
						id="preferences-btn"
						className="inline-flex items-center justify-center gap-1 sm:gap-2 px-2.5 sm:px-5 py-2 rounded-lg font-semibold text-[11px] sm:text-sm cursor-pointer transition-all duration-200 bg-slate-700 border border-border-main text-text-main min-w-[72px] sm:min-w-[100px] hover:bg-slate-600 hover:-translate-y-0.5 active:translate-y-0 active:scale-95 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
						title="設定 (Alt+S)"
						aria-label="設定を開く"
						onClick={onPreferences}
						disabled={isStopping}
					>
						<span role="img" aria-hidden="true">
							⚙️
						</span>{" "}
						<span className="truncate">設定</span>
						<kbd className="hidden md:inline-flex ml-1 px-1.5 py-0.5 text-[10px] bg-white/20 rounded font-mono border-none">
							Alt+S
						</kbd>
					</button>
					<button
						type="button"
						id="history-btn"
						className="inline-flex items-center justify-center gap-1 sm:gap-2 px-2.5 sm:px-5 py-2 rounded-lg font-semibold text-[11px] sm:text-sm cursor-pointer transition-all duration-200 bg-slate-700 border border-border-main text-text-main min-w-[72px] sm:min-w-[100px] hover:bg-slate-600 hover:-translate-y-0.5 active:translate-y-0 active:scale-95 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
						title="履歴 (Alt+H)"
						aria-label="履歴を表示"
						onClick={onHistory}
						disabled={isStopping}
					>
						<span role="img" aria-hidden="true">
							{UI_ICONS.HISTORY}
						</span>{" "}
						<span className="truncate">履歴</span>
						<kbd className="hidden md:inline-flex ml-1 px-1.5 py-0.5 text-[10px] bg-white/20 rounded font-mono border-none">
							Alt+H
						</kbd>
					</button>
				</div>
				<div className="mt-2 flex gap-4 text-[10px] text-text-dim">
					<button
						type="button"
						className="hover:text-text-main cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded px-1 transition-all active:scale-95"
						onClick={() => onLegal?.("terms")}
					>
						利用規約
					</button>
					<button
						type="button"
						className="hover:text-text-main cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded px-1 transition-all active:scale-95"
						onClick={() => onLegal?.("privacy")}
					>
						プライバシーポリシー
					</button>
					<span>&copy; 2026 MonologLive</span>
				</div>
			</footer>
		);
	},
);
